import { useState, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useTheme } from 'styled-components';
import { RootReducer } from 'redux/reducers';
import { getFrameBounds } from 'redux/db/util';
import { DEFAULT_CANVAS_DROP_SHADOW_MODE, selectActiveScene } from 'redux/db/reducers';
import { Frame, FrameStatus } from 'dragonmouse-shared/interfaces';
import { Rect } from 'dragonmouse-shared/geometry';
import * as S from './styles';
import * as Base from '../styles';
import Icon, { IconType } from 'components/Icon';
import { Loader } from 'components/Widget/Loader';
import { useImageLoading } from 'hooks/useImageLoading';
import { selectTransform } from 'redux/mode/reducers';
import { frameImageUrl } from 'utils';

interface Props {
	frame: Frame;
}

const FrameView = ({ frame }: Props) => {
	const theme = useTheme();

	// the rectangle, in canvas coordinates, that will be used to draw this frame
	// the header is not included in this rectangle
	const [bounds, setBounds] = useState<Rect>({ x: 0, y: 0, width: 0, height: 0 });
	const [displayStatus, setDisplayStatus] = useState('');
	const imageSrc = frameImageUrl(frame);
	const [isImageLoading, handleImageRef] = useImageLoading(imageSrc ?? '');

	const { scene, transform } = useSelector(
		(r: RootReducer) => ({
			scene: selectActiveScene(r.db, r.mode.activeSceneId),
			transform: selectTransform(r.mode),
		}),
		shallowEqual
	);

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------

	// reposition the frame when needed
	useEffect(() => {
		setBounds(getFrameBounds(frame));
	}, [frame.x, frame.y, frame.width, frame.height]);

	useEffect(() => {
		// if there's no status, clear displayStatus
		if (!frame.metadata.statusMessage) {
			setDisplayStatus('');
			return;
		}

		const MAX_STATUS_LENGTH = 64;

		// if status is set, start "typing" it into displayStatus
		setDisplayStatus('');
		let i = 0;
		const interval = setInterval(() => {
			i++;
			setDisplayStatus(frame.metadata.statusMessage.slice(0, Math.min(i, MAX_STATUS_LENGTH)));
			if (i >= frame.metadata.statusMessage.length) {
				clearInterval(interval);
			}
		}, 50);

		return () => clearInterval(interval);
	}, [frame.metadata.statusMessage]);

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	const renderContents = () => {
		return (
			<>
				{imageSrc && (
					<img
						// this key is important to manage image loading correctly
						key={imageSrc}
						ref={handleImageRef}
						src={imageSrc}
						width={bounds.width}
						height={bounds.height}
					/>
				)}
			</>
		);
	};

	const renderStatusIndicator = () => {
		if (frame.metadata.status === FrameStatus.ERROR) {
			return <Icon type={IconType.ERROR} width={60} height={60} color={theme.gray[1000]} />;
		} else if (frame.metadata.status === FrameStatus.PROCESSING || isImageLoading) {
			return (
				<S.FrameViewLoader>
					<Loader width={85} height={85} />
				</S.FrameViewLoader>
			);
		}
		return null;
	};

	const renderOverlay = () => {
		return (
			<S.Overlay>
				<S.StatusIndicator>{renderStatusIndicator()}</S.StatusIndicator>
				<S.OverlayText aboveFullZoom={transform.scale > 1}>
					{displayStatus || (isImageLoading && 'Loading frame...')}
				</S.OverlayText>
			</S.Overlay>
		);
	};

	return (
		<>
			<S.Base sceneBounds={bounds} backgroundColor={frame.metadata.backgroundColor}>
				{renderContents()}
				{(frame.metadata.status !== FrameStatus.READY || isImageLoading) && renderOverlay()}
			</S.Base>
			<Base.DropShadow
				sceneBounds={bounds}
				dropShadowMode={scene?.metadata?.canvasDropShadowMode || DEFAULT_CANVAS_DROP_SHADOW_MODE}
			/>
		</>
	);
};

export default FrameView;
